/**
 * Drukattest photo row (per blok).
 *
 * Extracted verbatim from CollectorenTab so both the legacy tab and the project
 * Dashboard render an identical per-blok drukattest strip (compact 40px thumbs,
 * upload + delete gated by `photo_upload`, lightbox). Behaviour is unchanged from
 * the original inline component.
 */
import { useEffect, useRef, useState } from 'react';
import type { PhotoItem } from '@/types/odoo';
import { usePermissionsStore } from '@/stores/permissions';
import { PhotoLightbox } from '@/components/ui/PhotoLightbox';
import { compressImage } from '@/utils/imageCompression';
import { isPdfPhoto } from '@/utils/photoFiles';
import { PhotoGridThumb } from '@/components/ui/PhotoGridThumb';
import { uploadPool } from '@/utils/uploadPool';
import { describeUploadFailures } from '@/utils/uploadFeedback';
import { listDrukattestPhotos, uploadDrukattestPhoto, deleteDrukattestPhoto } from '@/api/installer';

export function DrukattestRow({
  projectId,
  projectName,
  blokName,
  refreshKey = 0,
  prefetchedPhotos,
}: {
  projectId: number;
  projectName: string;
  blokName: string;
  refreshKey?: number;
  prefetchedPhotos?: PhotoItem[];
}) {
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canFeature = usePermissionsStore((s) => s.canFeature);
  const canUpload = canFeature('photo_upload');

  useEffect(() => {
    // Use prefetched photos on initial load to avoid per-blok API call
    if (prefetchedPhotos && refreshKey === 0) {
      setPhotos(prefetchedPhotos);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    listDrukattestPhotos(projectId, projectName, blokName)
      .then((items) => {
        if (!cancelled) setPhotos(items);
      })
      .catch(() => {
        // Silently fail — row just won't show
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [projectId, projectName, blokName, refreshKey, prefetchedPhotos]);

  const handleDelete = async (photo: PhotoItem) => {
    if (!confirm('Foto verwijderen?')) return;
    setDeletingKey(photo.key);
    setDeleteError(null);
    try {
      await deleteDrukattestPhoto(projectId, photo.key);
      setPhotos((prev) => prev.filter((p) => p.key !== photo.key));
    } catch (err) {
      console.error('Delete drukattest photo failed:', err);
      setDeleteError('Foto verwijderen mislukt');
    } finally {
      setDeletingKey(null);
    }
  };

  // Auto-clear delete error after 4s
  useEffect(() => {
    if (!deleteError) return;
    const t = setTimeout(() => setDeleteError(null), 4000);
    return () => clearTimeout(t);
  }, [deleteError]);

  const handleUpload = async (files: FileList) => {
    const fileArr = Array.from(files);
    setUploading(true);
    setUploadError(null);
    setUploadProgress({ done: 0, total: fileArr.length });

    const { failed, failures } = await uploadPool(fileArr, async (file) => {
      const compressed = await compressImage(file);
      const result = await uploadDrukattestPhoto(projectId, compressed, projectName, blokName);
      setPhotos((prev) => [...prev, {
        key: result.key,
        url: '', // will be refreshed
        filename: file.name,
        lastModified: new Date().toISOString(),
        size: compressed.size,
      }]);
      setUploadProgress((prev) => prev ? { ...prev, done: prev.done + 1 } : null);
    }, 2);

    // Refresh from server to get correct URLs
    try {
      const items = await listDrukattestPhotos(projectId, projectName, blokName);
      setPhotos(items);
    } catch {
      // keep optimistic state
    }

    setUploading(false);
    setUploadProgress(null);
    if (fileInputRef.current) fileInputRef.current.value = '';

    if (failed > 0) {
      setUploadError(describeUploadFailures(failed, failures));
    }
  };

  // PhotoLightbox is an image zoom/pan viewer; a PDF drukattest opens in a new
  // tab instead, so it must not appear in the lightbox list or in its index math.
  const imagePhotos = photos.filter((p) => !isPdfPhoto(p));

  // Hide entirely when loading or no photos and no upload permission
  if (loading || (photos.length === 0 && !canUpload)) return null;

  return (
    <div className="py-2 px-3 rounded-lg bg-white border border-gray-200">
      <div className="flex items-center gap-2">
        <p className="text-[10px] font-bold text-td-muted uppercase tracking-wide shrink-0">Drukattest</p>

        <div className="flex items-center gap-1.5 overflow-x-auto">
          {photos.map((p, idx) => (
            <div key={p.key} className="relative group shrink-0">
              <PhotoGridThumb
                photo={p}
                variant="strip"
                imgClassName="w-10 h-10 object-cover rounded border border-gray-200 hover:border-td-blue transition-colors"
                openLabel={`Drukattest foto ${idx + 1} openen`}
                onOpen={() => {
                  setLightboxIndex(imagePhotos.findIndex((i) => i.key === p.key));
                  setLightboxOpen(true);
                }}
              />
              {canUpload && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleDelete(p); }}
                  disabled={deletingKey === p.key}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-red-50 border border-gray-200 disabled:opacity-50"
                  aria-label={`Drukattest foto ${p.filename} verwijderen`}
                  title="Foto verwijderen"
                >
                  {deletingKey === p.key ? (
                    <div className="w-2.5 h-2.5 border border-red-400 border-t-transparent rounded-full animate-spin" role="status">
                      <span className="sr-only">Verwijderen...</span>
                    </div>
                  ) : (
                    <svg className="w-2.5 h-2.5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </button>
              )}
            </div>
          ))}
          {canUpload && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-10 h-10 border-2 border-dashed border-gray-300 rounded flex flex-col items-center justify-center hover:border-td-blue transition-colors disabled:opacity-50 shrink-0"
              aria-label="Drukattest foto of PDF uploaden"
            >
              {uploading ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
                    <span className="sr-only">Uploaden...</span>
                  </div>
                  {uploadProgress && (
                    <span className="text-[8px] text-td-muted leading-none mt-0.5">
                      {uploadProgress.done}/{uploadProgress.total}
                    </span>
                  )}
                </>
              ) : (
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              )}
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.heic,.webp,.pdf,application/pdf"
            multiple
            onChange={(e) => e.target.files && e.target.files.length > 0 && handleUpload(e.target.files)}
            className="hidden"
            aria-label="Selecteer drukattest foto's of PDF-bestanden om te uploaden"
          />
        </div>

        {photos.length > 0 && (
          <span className="text-[10px] font-bold text-td-muted shrink-0">{photos.length}</span>
        )}
      </div>

      {uploadError && (
        <p className="text-xs text-red-500 mt-1">{uploadError}</p>
      )}
      {deleteError && (
        <p className="text-xs text-red-500 mt-1">{deleteError}</p>
      )}

      {lightboxOpen && imagePhotos.length > 0 && (
        <PhotoLightbox
          photos={imagePhotos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}
